import {useEffect, useState, useMemo, useCallback, useRef} from 'react';
import {t} from 'i18next';
import {RootState} from '../../../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import {
  setProjectViewTableState,
  setProjectViewColumnState,
} from '../../../../redux/slices/welcomeScreenProjectViewSlice';
import {Box, Typography, Grid, Stack, Divider} from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  GridFilterItem,
  GridFilterModel,
  GridRowId,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {ReactComponent as ExportIcon} from '../../../../assests/svg/export_icon.svg';
import {TABLE_PAGE_SIZE_OPTION} from '../../../../constants/constants';
import AurDataGridPro from '../../../common/aur-data-grid-pro/AurDataGridPro';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurNoResults from '../../../common/aur-no-results/AurNoResults';
import AurButton from '../../../common/aur-button/AurButton';
import useServerSideDataGrid, {
  AurDataGridFilterState,
  GetTableData,
  toODataQuery,
} from '../../../common/aur-data-grid-pro/useServerSideDataGrid';
import {
  exportProjectListXlsx,
  getProjectList,
} from '../../../../api/ClientManagementService';
import {
  pinProjectInAllActiveGrid,
  unpinProjectInAllActiveGrid,
} from '../../../../api/UserManagementService';
import {
  IProjectData,
  IFilterData,
} from '../../../../types/client-management/cientManagementTypes';
import {getProjectTableColumnDef} from './projectTableColumnDef';
import AurPagination from '../../../common/aur-pagination/AurPagination';
import AurIconButton from '../../../common/aur-button/AurIconButton';
import ManageColumnsDrawer, {IColumn} from './ManageColumnsDrawer';
import {ProjectViewColumnState} from './ProjectViewColumnState';
import {
  normalizeColumnState,
  toColumnStateKey,
  toColumnVisibilityModel,
  toGridField,
} from './projectViewColumnMapping';
import {
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
} from '../../../../utils/utils';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import ProjectDetails from './ProjectDetails';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {QSI_BTN_IDS} from '../../../../constants/qualtricsConstants';

const defaultFilterState: AurDataGridFilterState = {
  searchState: {
    items: [],
  },
  sortState: [{field: 'projectName', sort: 'asc'}],
  paginationState: {
    page: 1,
    pageSize: 25,
  },
};

const defaultColumnState: ProjectViewColumnState = {
  clientName: true,
  projectName: true,
  icons: true,
  engagementCode: true,
  alphaCode: true,
  clientPreferredId: true,
  domainCountryCode: true,
  highlyConfidential: false,
  ecaEnabled: false,
  hasAlerts: false,
  branchName: true,
  departmentName: true,
  dataDeliveries: true,
  datasetsProcessing: false,
  processedDatasets: true,
  productionsCompleted: true,
  reviewWorkspaceUsers: true,
  totalDataUnderManagement: true,
};

const exportColumnTranslationKey =
  'welcomeScreenMultiClient.projectViewTab.exportColumns';

const getExportColumnMapping = (): Record<
  keyof ProjectViewColumnState,
  string[]
> => ({
  clientName: [t(`${exportColumnTranslationKey}.clientName`)],
  projectName: [t(`${exportColumnTranslationKey}.projectName`)],
  icons: [
    t(`${exportColumnTranslationKey}.highlyConfidential`),
    t(`${exportColumnTranslationKey}.ecaEnabled`),
    t(`${exportColumnTranslationKey}.hasAlerts`),
  ],
  engagementCode: [t(`${exportColumnTranslationKey}.engagementCode`)],
  alphaCode: [t(`${exportColumnTranslationKey}.alphaCode`)],
  clientPreferredId: [t(`${exportColumnTranslationKey}.clientPreferredId`)],
  domainCountryCode: [t(`${exportColumnTranslationKey}.domainCountryCode`)],
  highlyConfidential: [t(`${exportColumnTranslationKey}.highlyConfidential`)],
  ecaEnabled: [t(`${exportColumnTranslationKey}.ecaEnabled`)],
  hasAlerts: [t(`${exportColumnTranslationKey}.hasAlerts`)],
  branchName: [t(`${exportColumnTranslationKey}.branchName`)],
  departmentName: [t(`${exportColumnTranslationKey}.departmentName`)],
  dataDeliveries: [t(`${exportColumnTranslationKey}.dataDeliveries`)],
  datasetsProcessing: [t(`${exportColumnTranslationKey}.datasetsProcessing`)],
  processedDatasets: [t(`${exportColumnTranslationKey}.processedDatasets`)],
  productionsCompleted: [
    t(`${exportColumnTranslationKey}.productionsCompleted`),
  ],
  reviewWorkspaceUsers: [
    t(`${exportColumnTranslationKey}.reviewWorkspaceUsers`),
  ],
  totalDataUnderManagement: [
    t(`${exportColumnTranslationKey}.totalDataUnderManagement`),
  ],
});

const defaultInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
};

const ProjectViewTab = () => {
  const {trackGAEvent} = useAnalytics();

  const [isExportingData, setIsExportingData] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [resultData, setResultData] = useState<
    IProjectData[] | null | undefined
  >();
  const [filterData, setFilterData] = useState<
    IFilterData | null | undefined
  >();
  const [pinnedState, setPinnedState] = useState<Record<string, boolean>>({});
  const [politeAnnouncement, setPoliteAnnouncement] = useState('');
  const [assertiveAnnouncement, setAssertiveAnnouncement] = useState('');
  const {showToast} = useToast();

  const previousFilterModelRef = useRef<GridFilterModel>({items: []});

  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const cachedFilterState: AurDataGridFilterState =
    useSelector(
      (state: RootState) => state.welcomeScreenProjectView.projectViewTableState
    ) ?? defaultFilterState;

  const initialState = cachedFilterState
    ? {
        filter: {
          filterModel: {
            items: cachedFilterState.searchState.items,
          },
        },
      }
    : defaultInitialState;

  const cachedColumnState: ProjectViewColumnState =
    useSelector(
      (state: RootState) =>
        state.welcomeScreenProjectView.projectViewColumnState
    ) ?? defaultColumnState;

  const normalizedColumnState = useMemo(
    () => normalizeColumnState(cachedColumnState, defaultColumnState),
    [cachedColumnState]
  );

  const columnVisibilityModel = useMemo(
    () => toColumnVisibilityModel(normalizedColumnState),
    [normalizedColumnState]
  );

  const onClickTrackingEvent = (
    name: string,
    action: string,
    type: string,
    region: string,
    subPage?: {
      subpage_name: string;
      subpage_type: string;
    },
    eventName?: string
  ) => {
    const eventObject: any = {
      eventName: eventName ?? GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type,
        region,
        action,
      },
    };

    if (subPage) {
      eventObject.subPage = subPage;
    }

    trackGAEvent(eventObject);
  };

  const getProjectListODataQuery = (state: AurDataGridFilterState): string => {
    const booleanFieldFormatter = (item: GridFilterItem): string =>
      `${item.field} ${item.operator} ${item.value}`;
    const listFieldFormatter = (item: GridFilterItem): string =>
      `${item.field} in (${item.value.map((v: string) => `'${v}'`).join(', ')})`;

    const customFieldFormatter = {
      highlyConfidential: booleanFieldFormatter,
      ecaEnabled: booleanFieldFormatter,
      hasAlerts: booleanFieldFormatter,
      domainCountryCode: listFieldFormatter,
    };

    return toODataQuery(
      state.searchState,
      state.sortState,
      state.paginationState,
      customFieldFormatter
    );
  };

  const getExportSelectedColumns = (): string[] => {
    const exportColumnMapping = getExportColumnMapping();
    const orderedVisibleColumns = (
      Object.keys(exportColumnMapping) as Array<keyof ProjectViewColumnState>
    ).filter((key) => normalizedColumnState[key] === true);
    const selectedColumns = orderedVisibleColumns.flatMap(
      (columnKey) => exportColumnMapping[columnKey]
    );
    return [...new Set(selectedColumns)];
  };

  const buildExportQuery = (state: AurDataGridFilterState): string => {
    const queryParams = new URLSearchParams(
      getProjectListODataQuery(state).replace(/^\?/, '')
    );
    getExportSelectedColumns().forEach((column) => {
      queryParams.append('selectedColumns', column);
    });
    return `?${queryParams.toString()}`;
  };

  const getFilteredProjects: GetTableData<IProjectData> = async (
    filterState: AurDataGridFilterState
  ) => {
    try {
      const oDataQuery = getProjectListODataQuery(filterState);

      const result = await getProjectList(oDataQuery);
      const resultData = result?.data?.data;

      // resultData looks like this
      // {
      //   data: [],
      //   filterData: {},
      //   hasNextPage: boolean,
      //   hasPreviousPage: boolean,
      //   page: number,
      //   pageSize: number,
      //   totalCount: number,
      //   totalPages: number,
      // }
      setResultData(resultData?.data);
      setFilterData(resultData?.filterData);
      setCurrentPage(resultData?.page);

      return {
        data: resultData?.data || [],
        totalCount: resultData?.totalCount || 0,
      };
    } catch (e) {
      console.error('Error fetching projects:', e);
      return {data: [], totalCount: 0};
    }
  };

  const {totalRowCount, rowsData, filterState, isLoading, serverSideEvents} =
    useServerSideDataGrid<IProjectData>(getFilteredProjects, cachedFilterState);

  useEffect(() => {
    dispatch(setProjectViewTableState(filterState));
  }, [filterState]);

  useEffect(() => {
    const nextPinnedState = rowsData.reduce<Record<string, boolean>>(
      (acc, row) => {
        acc[row.projectId] = row.pinned;
        return acc;
      },
      {}
    );
    setPinnedState(nextPinnedState);
  }, [rowsData]);

  const [detailPanelContent, setDetailPanelContent] = useState(<></>);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    GridRowId[]
  >([]);

  const getDetailPanelContent = () => {
    return <>{detailPanelContent}</>;
  };

  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (ids: GridRowId[]) => {
      // This ensures only one panel is expanded at a time
      const newIds = ids.length > 1 ? [ids[ids.length - 1]] : ids;
      setDetailPanelExpandedRowIds(newIds);

      // Load the panel details
      if (newIds.length > 0) {
        const projectId = String(newIds[0]);
        const project = resultData?.find((i) => i.projectId === projectId);
        const alphaCode = project?.alphaCode ?? '';
        const preferredId = project?.clientPreferredId ?? '';
        setDetailPanelContent(
          <ProjectDetails
            projectId={projectId}
            alphaCode={alphaCode}
            preferredId={preferredId}
            columnCount={visibleColumnCount}
          />
        );
      }
    },
    [resultData]
  );

  const handleProjectPinned = async (projectId: string) => {
    const project = rowsData.find((row) => row.projectId === projectId);
    if (!project?.clientId) return;

    try {
      await pinProjectInAllActiveGrid(projectId, project.clientId);
      setPinnedState((prev) => ({...prev, [projectId]: true}));
    } catch (error) {
      console.error('Error pinning project:', error);
    }
  };

  const handleProjectUnpinned = async (projectId: string) => {
    const project = rowsData.find((row) => row.projectId === projectId);
    if (!project?.clientId) return;

    try {
      await unpinProjectInAllActiveGrid(projectId, project.clientId);
      setPinnedState((prev) => ({...prev, [projectId]: false}));
    } catch (error) {
      console.error('Error unpinning project:', error);
    }
  };

  const handleExportClick = () => {
    exportTable();
  };

  const handleExportKeyDown = (event: React.KeyboardEvent<Element>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      exportTable();
    }
  };

  const exportTable = async () => {
    if (isExportingData || !filterState || totalRowCount <= 0) {
      return;
    }

    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
      file_extension: GA_FILE_EXTENTIONS.XLSX,
      click: {
        name: t('gaTracking.global.exportReport'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.global.downloadedReport'),
      },
    });

    try {
      setIsExportingData(true);
      setAssertiveAnnouncement('');
      setPoliteAnnouncement(
        t('welcomeScreenMultiClient.projectViewTab.reportGenerating')
      );

      const exportQuery = buildExportQuery(filterState);
      const response = await exportProjectListXlsx(exportQuery);
      const contentDispositionHeader =
        response?.headers?.['content-disposition'] ??
        response?.headers?.['Content-Disposition'] ??
        '';
      const fileNameFromApi =
        getFilenameFromHeaders(contentDispositionHeader) ??
        `Project_View_Report_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
      downloadResponseAsXLSX(response, fileNameFromApi, false);

      showToast(
        t('welcomeScreenMultiClient.projectViewTab.reportDownloadSuccess'),
        'success'
      );
      setPoliteAnnouncement(
        t('welcomeScreenMultiClient.projectViewTab.reportDownloadSuccess')
      );
    } catch (error) {
      console.error('Project list export failed:', error);
      const errorMessage = t(
        'welcomeScreenMultiClient.projectViewTab.reportDownloadFailed'
      );
      showToast(errorMessage, 'error');
      setAssertiveAnnouncement(errorMessage);
    } finally {
      setIsExportingData(false);
    }
  };

  const columns = useMemo(() => {
    return getProjectTableColumnDef(
      apiRef,
      cachedFilterState,
      filterData,
      pinnedState,
      handleProjectPinned,
      handleProjectUnpinned
    );
  }, [
    apiRef,
    cachedFilterState,
    filterData,
    pinnedState,
    handleProjectPinned,
    handleProjectUnpinned,
  ]);

  useEffect(() => {
    if (isDrawerVisible) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t(
            'gaTracking.projectViewTab.manageColumnsAndSortOrder'
          ),
          subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        },
      });
    }
  }, [isDrawerVisible]);

  const visibleColumnCount = useMemo(() => {
    const visibleColumns = columns?.filter((column) => {
      const columnStateKey = toColumnStateKey(column.field);

      return normalizedColumnState[columnStateKey] !== false;
    });

    // +1 because MUI adds the detail panel toggle column: __detail_panel_toggle__

    return visibleColumns?.length + 1;
  }, [columns, normalizedColumnState]);

  const [itemsPerPage, setItemsPerPage] = useState<number>(
    filterState?.paginationState.pageSize ?? 25
  );
  const [currentPage, setCurrentPage] = useState<number>(
    filterState?.paginationState.page ?? 1
  );

  const setItemsPerPageHandler = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    apiRef.current.setPageSize(itemsPerPage);

    setCurrentPage(1);
    apiRef.current.setPage(0);

    serverSideEvents?.handlePaginationChange({
      page: 1,
      pageSize: itemsPerPage,
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    apiRef.current.setPage(page - 1);

    serverSideEvents?.handlePaginationChange({
      page: page,
      pageSize: itemsPerPage,
    });
  };

  const renderPagination = () => {
    return (
      <Stack
        direction="row"
        spacing={2}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Box sx={{width: '100%'}}>
          <AurPagination
            totalItems={totalRowCount}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            perPageOptions={TABLE_PAGE_SIZE_OPTION}
            setItemsPerPage={setItemsPerPageHandler}
            onPageChange={handlePageChange}
            itemType={t('common.projectsPagination')}
            gaRegion={GA_REGIONS.BODY}
            className="project-view_page-control"
          />
        </Box>
        <Box>
          <AurIconButton
            size="small"
            icon={<PlaylistAddIcon aria-hidden="true" />}
            ariaLabel={t('common.pagination.manageColumns')}
            className="project-view_manage-columns"
            dataTestId="project-view_manage-columns"
            onClick={() => {
              onClickTrackingEvent(
                t('gaTracking.projectViewTab.manageColumns&SortOrder'),
                t('gaTracking.projectViewTab.drawerManageColumnsAndSortOrder'),
                GA_CLICK_TYPES.ICON,
                GA_REGIONS.BODY,
                undefined,
                GA_EVENT_NAMES.CLICK
              );
              setIsDrawerVisible(true);
            }}
          />
        </Box>
      </Stack>
    );
  };

  const getDrawerColumns = (): IColumn[] => {
    const columns: IColumn[] = [
      {
        name: 'clientName',
        label: t('welcomeScreenMultiClient.projectTable.header.clientName'),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'projectName',
        label: t('welcomeScreenMultiClient.projectTable.header.projectName'),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'icons',
        label: t('welcomeScreenMultiClient.projectTable.header.status'),
        isSortable: false,
        isVisible: true,
      },
      {
        name: 'engagementCode',
        label: t('welcomeScreenMultiClient.projectTable.header.engagementCode'),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'alphaCode',
        label: t('welcomeScreenMultiClient.projectTable.header.alphaCode'),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'clientPreferredId',
        label: t(
          'welcomeScreenMultiClient.projectTable.header.clientPreferredId'
        ),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'domainCountryCode',
        label: t(
          'welcomeScreenMultiClient.projectTable.header.domainCountryCode'
        ),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'branchName',
        label: t('welcomeScreenMultiClient.projectTable.header.branchName'),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'departmentName',
        label: t('welcomeScreenMultiClient.projectTable.header.departmentName'),
        isSortable: true,
        isVisible: true,
      },
      {
        name: 'dataDeliveriesCount',
        label: t('welcomeScreenMultiClient.projectTable.header.dataDeliveries'),
        isSortable: false,
        isVisible: true,
      },
      // {
      //   name: 'dataProcessingGb',
      //   label: t('welcomeScreenMultiClient.projectTable.header.datasetsProcessing'),
      //   isSortable: false,
      //   isVisible: true,
      // },
      {
        name: 'processedDatasetsGb',
        label: t(
          'welcomeScreenMultiClient.projectTable.header.processedDatasets'
        ),
        isSortable: false,
        isVisible: true,
      },
      {
        name: 'productionsCompletedCount',
        label: t(
          'welcomeScreenMultiClient.projectTable.header.productionsCompleted'
        ),
        isSortable: false,
        isVisible: true,
      },
      {
        name: 'reviewWorkspaceUsersCount',
        label: t(
          'welcomeScreenMultiClient.projectTable.header.reviewWorkspaceUsers'
        ),
        isSortable: false,
        isVisible: true,
      },
      {
        name: 'totalDataUnderManagementGb',
        label: t(
          'welcomeScreenMultiClient.projectTable.header.totalDataUnderManagement'
        ),
        isSortable: false,
        isVisible: true,
      },
    ];

    // Set Selection
    const properties = Object.keys(defaultColumnState) as Array<
      keyof ProjectViewColumnState
    >;
    for (const property of properties) {
      const formattedColumn = columns.find(
        (c) => toColumnStateKey(c.name) === property
      );
      if (formattedColumn) {
        formattedColumn.isVisible = Boolean(normalizedColumnState[property]);
      }
    }

    // Set Sort
    const currentSort = cachedFilterState.sortState;
    for (let i = 0; i < currentSort.length; i++) {
      const sortedColumn = currentSort[i];
      const formattedColumn = columns.find(
        (c) => c.name === sortedColumn.field
      );
      if (formattedColumn) {
        formattedColumn.ordinal = i;
        formattedColumn.order = sortedColumn.sort === 'asc' ? 'asc' : 'desc';
      }
    }

    return columns;
  };

  const handleSaveColumns = (
    selectedColumns: IColumn[],
    sortedColumns: IColumn[]
  ) => {
    // Save Selection
    const allColumns = getDrawerColumns();
    const visibleColumns = allColumns.filter((i) =>
      selectedColumns.some((c) => c.name === i.name)
    );
    const hiddenColumns = allColumns.filter(
      (i) => !selectedColumns.some((c) => c.name === i.name)
    );

    const newColumnState: ProjectViewColumnState = {...defaultColumnState};
    for (const visibleColumn of visibleColumns) {
      const columnKey = toColumnStateKey(visibleColumn.name);
      newColumnState[columnKey] = true;
    }
    for (const hiddenColumn of hiddenColumns) {
      const columnKey = toColumnStateKey(hiddenColumn.name);
      newColumnState[columnKey] = false;
    }
    dispatch(setProjectViewColumnState(newColumnState));

    // Save Sort
    if (sortedColumns && sortedColumns.length > 0) {
      const sortModel = sortedColumns.map((i) => {
        return {field: toGridField(toColumnStateKey(i.name)), sort: i.order};
      });
      apiRef.current.setSortModel(sortModel);
      serverSideEvents?.handleSortChange(sortModel);
    } else {
      const sortModel = defaultFilterState.sortState;
      apiRef.current.setSortModel(sortModel);
      serverSideEvents?.handleSortChange(sortModel);
    }

    setIsDrawerVisible(false);
  };

  const getNoRowsOverlay = () => {
    if (isLoading) {
      return AurDataGridLoadingOverlay;
    }

    if (totalRowCount <= 0) {
      return AurNoResults;
    }

    return undefined;
  };

  const callFilterGATrackingEvent = (items: GridFilterModel) => {
    const previousFilters = previousFilterModelRef.current.items.filter(
      (item) =>
        item.value !== undefined && item.value !== null && item.value !== ''
    );

    const currentFilters = items.items.filter(
      (item) =>
        item.value !== undefined && item.value !== null && item.value !== ''
    );

    const isFilterRemoved = currentFilters.length < previousFilters.length;

    if (!isFilterRemoved) {
      const changedFilter = currentFilters.find((current) => {
        const previous = previousFilters.find((p) => p.field === current.field);

        return !previous || previous.value !== current.value;
      });

      if (changedFilter) {
        const allColumns = getDrawerColumns();

        const selectedFilterName =
          changedFilter.id !== 0 ? changedFilter.field : 'icons';
        const column = allColumns.find(
          (col) =>
            toGridField(toColumnStateKey(col.name)) === selectedFilterName
        );

        if (column) {
          const isDropdown =
            column.name === 'icons' || column.name === 'domainCountryCode';

          const action = isDropdown
            ? `selected - ${column.label}`
            : t('gaTracking.projectViewTab.filteredList');

          let trackingName = isDropdown
            ? changedFilter.id === 0
              ? t(`gaTracking.projectViewTab.icons.${changedFilter.field}`)
              : changedFilter.value
            : t(`welcomeScreenMultiClient.projectTable.header.${column.name}`);

          // For domainCountryCode, convert the selected country code
          // to the corresponding country name.
          if (column.name === 'domainCountryCode') {
            const selectedCountryCode = changedFilter.value?.[0];

            const selectedCountry = filterData?.countries?.find(
              (country) => country.countryCode === selectedCountryCode
            );

            trackingName = selectedCountry?.countryName ?? selectedCountryCode;
          }

          onClickTrackingEvent(
            trackingName,
            action,
            isDropdown ? GA_CLICK_TYPES.DROPDOWN : GA_CLICK_TYPES.INPUT,
            GA_REGIONS.BODY,
            undefined,
            GA_EVENT_NAMES.FILTER
          );
        }
      }
    }

    previousFilterModelRef.current = items;
  };

  return (
    <>
      <Box className="project-view_tab-header">
        <Grid container direction="row" justifyContent="space-between">
          <Grid sx={{alignContent: 'end'}}>
            <Typography
              className="typo-text subhead2_size"
              data-testid="all-active-projects"
            >
              {t('welcomeScreenMultiClient.allActiveProjects')}
            </Typography>
          </Grid>
          <Grid>
            <AurButton
              type="submit"
              startIcon={
                isExportingData ? undefined : <ExportIcon aria-hidden="true" />
              }
              label={t('common.exportReport')}
              size="Medium"
              onClick={handleExportClick}
              onKeyDown={handleExportKeyDown}
              disabled={isExportingData || totalRowCount <= 0}
              showLoader={isExportingData}
              showLabelWithLoader
              disableHint={t(
                'welcomeScreenMultiClient.projectViewTab.exportButtonDisabledHint'
              )}
              dataTestId="export-project-list"
              id={QSI_BTN_IDS.PROJECT_VIEW.EXPORT}
            />
          </Grid>
        </Grid>
      </Box>
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {politeAnnouncement}
      </span>
      <span className="sr-only" aria-live="assertive" aria-atomic="true">
        {assertiveAnnouncement}
      </span>

      <Box className="project-view_tab-body">
        <Box className="project-view_pagination pagination_top">
          {renderPagination()}
        </Box>
        <Box className="project-view_project-grid">
          <AurDataGridPro
            className="project-view_grid-control"
            classNameToFindElement="project-view_grid-control" // Don't remove this and className as this used to find element ref using class selector
            rows={rowsData}
            apiRef={apiRef}
            columns={columns}
            entityType={t('common.projectsPagination')}
            primaryKey="projectId"
            disableColumnResize
            disableVirtualization
            checkBoxVisible={false}
            rowSelection={false}
            enableServerSide
            filterMode="server"
            serverSideEvents={serverSideEvents}
            totalRowCount={totalRowCount}
            noRowsOverlay={getNoRowsOverlay()}
            enableRowClickOnEnterPress
            getDetailPanelHeight={() => 'auto'}
            getDetailPanelContent={getDetailPanelContent}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={
              handleDetailPanelExpandedRowIdsChange
            }
            columnHeaderHeight={64}
            showPagination={false}
            defaultSortColumnName="projectName"
            headerFilters
            disableColumnFilter
            slots={{
              headerFilterMenu: null,
            }}
            filterState={filterState}
            onFilterModelChange={(items, details) => {
              if (details?.reason) {
                serverSideEvents?.handleFilterChange(items, 1000);
              }
              callFilterGATrackingEvent(items);
            }}
            initialState={initialState}
            filterDebounceMs={1000}
            applyFilterForReason
            columnVisibilityModel={columnVisibilityModel}
            slotProps={{
              headerFilterCell: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                  label: t(
                    'welcomeScreenMultiClient.projectTable.header.filter'
                  ),
                  className: 'project-view_text-filter',
                  dataTestid: 'project-view_text-filter',
                },
              },
            }}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
        </Box>
        <Box className="project-view_pagination pagination_bottom">
          {renderPagination()}
        </Box>
      </Box>

      {isLoading && <AurLoadingIndicator overlay isOpen={isLoading} />}

      {isDrawerVisible && (
        <ManageColumnsDrawer
          showDrawer={isDrawerVisible}
          columns={getDrawerColumns()}
          onClose={() => setIsDrawerVisible(false)}
          onSave={handleSaveColumns}
        />
      )}
    </>
  );
};

export default ProjectViewTab;
